import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { requestPOST } from 'src/utils/baseAPI';
import moment from 'moment';
import { useIntl } from 'react-intl';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const RenderChartMoney = ({ fromDateToDate, selectedBranch }) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [summary, setSummary] = useState({
    totalRevenue: 0,
    bicycleRevenue: 0,
    motorcycleRevenue: 0,
    carRevenue: 0,
    otherRevenue: 0,
  });
  const [rawData, setRawData] = useState([]);
  const intl = useIntl();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await requestPOST(`api/v1/dashboard/baidoxe_doanhthutheongay`, {
          tuNgay: fromDateToDate?.fromDate.clone().add(7, 'hours'),
          denNgay: fromDateToDate?.toDate.clone().add(7, 'hours'),
          branchId: selectedBranch ?? null,
        });

        const data = res?.data?.theoNgay ?? [];
        setRawData(data);
        
        // Chuẩn bị dữ liệu cho biểu đồ
        const labels = data.map(item => {
          if (!item.ngay) return '';
          const date = new Date(item.ngay);
          // Format ngắn gọn: DD/MM
          const day = date.getDate().toString().padStart(2, '0');
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          return `${day}/${month}`;
        }).filter(label => label !== '');
        const bicycleData = data.map(item => item.amountMoneyBicycle ?? 0);
        const motorcycleData = data.map(item => item.amountMoneyMotorcycle ?? 0);
        const carData = data.map(item => item.amountMoneyCar ?? 0);
        const otherData = data.map(item => item.amountMoneyOther ?? 0);

        // Tính tổng doanh thu cho từng ngày
        const totalData = data.map((item, index) => {
          return bicycleData[index] + motorcycleData[index] + carData[index] + otherData[index];
        });

        const bicycleTotal = bicycleData.reduce((total, amount) => total + amount, 0);
        const motorcycleTotal = motorcycleData.reduce((total, amount) => total + amount, 0);
        const carTotal = carData.reduce((total, amount) => total + amount, 0);
        const otherTotal = otherData.reduce((total, amount) => total + amount, 0);
        const amountMoneyTotal = bicycleTotal + motorcycleTotal + carTotal + otherTotal;

        // Cập nhật summary
        setSummary({
          totalRevenue: amountMoneyTotal,
          bicycleRevenue: bicycleTotal,
          motorcycleRevenue: motorcycleTotal,
          carRevenue: carTotal,
          otherRevenue: otherTotal,
        });

        setChartData({
          labels,
          datasets: [
            {
              label: 'xe đạp',
              data: bicycleData,
              borderColor: 'rgb(255, 99, 132)',
              backgroundColor: 'rgba(255, 99, 132, 0.5)',
              tension: 0.4,
              pointRadius: 5,
              pointHoverRadius: 8,
              borderWidth: 3,
              fill: false,
            },
            {
              label: 'xe máy',
              data: motorcycleData,
              borderColor: 'rgb(53, 162, 235)',
              backgroundColor: 'rgba(53, 162, 235, 0.5)',
              tension: 0.4,
              pointRadius: 5,
              pointHoverRadius: 8,
              borderWidth: 3,
              fill: false,
            },
            {
              label: 'xe oto',
              data: carData,
              borderColor: 'rgb(75, 192, 192)',
              backgroundColor: 'rgba(75, 192, 192, 0.5)',
              tension: 0.4,
              pointRadius: 5,
              pointHoverRadius: 8,
              borderWidth: 3,
              fill: false,
            },
            {
              label: 'khác',
              data: otherData,
              borderColor: 'rgb(255, 159, 64)',
              backgroundColor: 'rgba(255, 159, 64, 0.5)',
              tension: 0.4,
              pointRadius: 5,
              pointHoverRadius: 8,
              borderWidth: 3,
              fill: false,
            },
            {
              label: 'Tổng doanh thu',
              data: totalData,
              borderColor: 'rgb(220, 38, 127)',
              backgroundColor: 'rgba(220, 38, 127, 0.1)',
              tension: 0.4,
              pointRadius: 7,
              pointHoverRadius: 10,
              borderWidth: 4,
              fill: false,
              borderDash: [5, 5], // Đường gạch ngang để phân biệt
              pointStyle: 'circle',
              pointBorderWidth: 3,
              pointBackgroundColor: 'rgb(255, 255, 255)',
              pointBorderColor: 'rgb(220, 38, 127)',
            },
          ],
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [fromDateToDate, selectedBranch]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: 'Doanh thu theo ngày',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function(tooltipItems) {
            const label = tooltipItems[0].label;
            const dataIndex = tooltipItems[0].dataIndex;
            if (rawData && rawData[dataIndex] && rawData[dataIndex].ngay) {
              const date = new Date(rawData[dataIndex].ngay);
              return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
            }
            return label;
          },
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            const formattedValue = value.toLocaleString('vi-VN') + ' VND';
            
            if (label === 'Tổng doanh thu') {
              return `📊 ${label}: ${formattedValue}`;
            }
            return `${label}: ${formattedValue}`;
          },
          afterBody: function(tooltipItems) {
            const total = tooltipItems
              .filter(item => item.dataset.label !== 'Tổng doanh thu')
              .reduce((sum, item) => sum + item.parsed.y, 0);
            
            return `\n💰 Tổng tính: ${total.toLocaleString('vi-VN')} VND`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Ngày'
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          maxTicksLimit: 15,
          font: {
            size: 11
          },
          callback: function(value, index, values) {
            const label = this.getLabelForValue(value);
            if (values.length > 20) {
              return index % Math.ceil(values.length / 15) === 0 ? label : '';
            }
            return label;
          }
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value.toLocaleString('vi-VN') + ' VND';
          }
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        hoverRadius: 8,
      }
    }
  };

  return (
    <div className="card card-xl-stretch mb-xl-5">
      <div className="card-header min-h-50px">
        <h3 className="card-title fw-bold text-primary">Doanh thu theo ngày</h3>
        <div className="card-toolbar">
          <span className="badge badge-light-success fs-7">
            💰 Tổng: {summary.totalRevenue.toLocaleString('vi-VN')} VND
          </span>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="card-body pt-2 pb-4">
        <div className="row mb-4">
          <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
            <div className="card card-sm border border-success">
              <div className="card-body text-center">
                <div className="text-success fw-bold fs-6">{summary.totalRevenue.toLocaleString('vi-VN')}</div>
                <div className="text-muted fw-semibold fs-8">📊 Tổng doanh thu</div>
              </div>
            </div>
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
            <div className="card card-sm">
              <div className="card-body text-center">
                <div className="text-danger fw-bold fs-6">{summary.bicycleRevenue.toLocaleString('vi-VN')}</div>
                <div className="text-muted fw-semibold fs-8">🚲 Xe đạp</div>
              </div>
            </div>
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
            <div className="card card-sm">
              <div className="card-body text-center">
                <div className="text-primary fw-bold fs-6">{summary.motorcycleRevenue.toLocaleString('vi-VN')}</div>
                <div className="text-muted fw-semibold fs-8">🏍️ Xe máy</div>
              </div>
            </div>
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
            <div className="card card-sm">
              <div className="card-body text-center">
                <div className="text-info fw-bold fs-6">{summary.carRevenue.toLocaleString('vi-VN')}</div>
                <div className="text-muted fw-semibold fs-8">🚗 Xe oto</div>
              </div>
            </div>
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
            <div className="card card-sm">
              <div className="card-body text-center">
                <div className="text-warning fw-bold fs-6">{summary.otherRevenue.toLocaleString('vi-VN')}</div>
                <div className="text-muted fw-semibold fs-8">🚛 Khác</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Chart */}
      <div className="card-body pt-0">
        <div style={{ height: '400px', position: 'relative' }}>
        <Line options={options} data={chartData} />
        </div>
      </div>
    </div>
  );
};

export default RenderChartMoney; 